<template>
  <div class="profile-info-page" :class="showCollapse && 'collapse'">
    <div class="profile-info">
      <div v-if="!loading" class="profile-info-container">
        <div v-if="!isRecommended" class="breadcrumb-navigation">
          <span class="navigation" @click="handleBreadCrumbNavigationClick">{{ $t(`admin.layout.newLayout.menu.talentPool`) }}</span>
          <span class="split">/</span>
          <span class="navigation active">{{ $t(`admin.layout.newLayout.menu.applicantProfile`) }}</span>
        </div>
        <div v-if="!isRecommended" class="application-list-navigation">
          <el-button class="navigation-btn prev-btn" @click="handlePrevBtnClick">
            <i class="btn-icon el-icon-arrow-left"></i>
          </el-button>
          <span class="position">
            <span class="current">{{ (currentTalentIdIndex + 1)|formatBigNumber }}</span>
            <span class="split">/</span>
            <span class="total">{{ totalCount|formatBigNumber }}</span>
          </span>
          <el-button class="navigation-btn next-btn" @click="handleNextBtnClick">
            <i class="btn-icon el-icon-arrow-right"></i>
          </el-button>
          <RectLoading v-show="candidateIdListLoading" class="loading" ref="loading"></RectLoading>
        </div>
        <div class="base-info-container">
          <BaseInfo
            hiddenCandidateStatus
            :candidateInfo="candidateInfo"
            :statusGroupList="statusGroupList"
            :isTalentPool="true"
            @toRejectStatus="handleToRejectStatus"
            @changeStatus="handleChangeStatus"
          />
        </div>
        <div class="detail-info-container" ref="detailInfoContainer">
          <TabCard
            class="info-tab-card"
            :activeTab="activeTab"
            :tabList="tabList"
            @change="onTabChange"
          >
            <component v-bind="activeTab.props" v-on="activeTab.events" :is="activeTab.component" :isTalentPool="true" :candidateBaseInfo="candidateInfo"></component>
            <template #appliedJobsCount>
              <div v-if="showAppliedJobsCount" class="score-container">
                <el-tooltip
                  effect="light"
                  popper-class="light-tooltip-popper applied-jobs-tooltip"
                >
                  <template #content>
                    <div class="applied-jobs-tooltip-content">
                      <div class="tooltip-line total-line">{{ $t(`${i18nPrefix}.label.appliedJobsTotalLabel`) }}{{ appliedJobsCount }}</div>
                      <template v-if="recentAppliedJobs.length > 0">
                        <div class="tooltip-line section-title">{{ $t(`${i18nPrefix}.label.otherAppliedJobsLabel`) }}</div>
                        <div
                          v-for="(item, index) in recentAppliedJobs"
                          :key="`${item.job && item.job.id ? item.job.id : 'job'}_${index}`"
                          class="tooltip-job-item"
                        >
                          <span class="dot"></span>
                          <span v-auto-tooltip="{ content: item.job && item.job.i18nName }" class="title">{{ item.job && item.job.i18nName }}</span>
                        </div>
                      </template>
                    </div>
                  </template>
                  <div class="score-item applied-jobs-score-item">{{ appliedJobsCount }}</div>
                </el-tooltip>
              </div>
            </template>
          </TabCard>
        </div>
        <div v-if="!isRecommended" class="fixed-list-navigation" :style="fixedNavigationStyle">
          <el-button class="navigation-btn prev-btn" :disabled="currentTalentIdIndex === 0" @click="handlePrevBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
          <el-button class="navigation-btn next-btn" :disabled="(currentTalentIdIndex + 1) === totalCount" @click="handleNextBtnClick">
            <i class="btn-icon iconfont iconArrows"></i>
          </el-button>
        </div>
      </div>
      <CollapseInfo
        v-if="!loading && !candidateInfo.is_agency_uploaded_and_hid"
        ref="collapseInfo"
        class="collapse-container"
        :isTalentPool="true"
        :isMyComment="isMyComment"
        :candidateBaseInfo="candidateInfo"
        @open="handleOpen"
        @close="handleClose"
      />
    </div>
  </div>
</template>

<script>
import CollapseInfo from '@/views/adminNew/candidate/profile/components/collapseInfo.vue'
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, createI18nProperty } from '@/i18n'
import BaseInfo from '../profile/components/baseInfo.vue'
import TabCard from '../profile/components/tabCard.vue'
import Resume from '../profile/resume.vue'
import ActivityTracker from '../profile/activityTracker.vue'
import Tag from '../profile/tag.vue'
import RectLoading from '@/components/loading/rectLoading.vue'
import moment from 'moment'
import { ROOT_COMPANY_ID } from '@/constant/company'

const PAGE_TYPE = {
  RESUME: 'resume',
  ACTIVITY_TRACKER: 'activity-tracker',
  TAG: 'tag'
}

export default {
  components: {
    CollapseInfo,
    BaseInfo,
    TabCard,
    Resume,
    RectLoading
  },
  metaInfo () {
    return {
      title: this.pageTitle
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      loading: false,
      activeTab: null,
      pageDataLoader: null,
      candidateInfo: null,
      statusGroupList: [],
      statusList: [],
      defaultStatusKey: null,
      talentIdList: [],
      candidateIdListLoading: false,
      fixedNavigationRightResetter: null,
      fixedNavigationRight: 110,
      showCollapse: false,
      isMyComment: false,
      activityTrackerData: null,
      candidateAppliedJobs: [],
      appliedJobsLoaded: false
    }
  },
  computed: {
    isRecommended () {
      return this.$route.query.is_recommended === '1'
    },
    fixedNavigationStyle () {
      return {
        right: `${this.fixedNavigationRight}px`
      }
    },
    hasAssessmentPermission () {
      return this.hasAnyPermission('PREMIUM_ASSESSMENTS')
    },
    hasSatisfactionSurveyCandidate () {
      return this.hasAnyPermission('PREMIUM_SATISFACTION_SURVEY_CANDIDATE')
    },
    appliedJobsCount () {
      return this.candidateAppliedJobs.length
    },
    recentAppliedJobs () {
      return _
        .chain(this.candidateAppliedJobs)
        .orderBy(item => moment(item.apply_time).valueOf() || 0, ['desc'])
        .take(2)
        .value()
    },
    showAppliedJobsCount () {
      return this.appliedJobsLoaded && this.appliedJobsCount > 0
    },
    tabList () {
      const tabList = [
        {
          pageType: PAGE_TYPE.RESUME,
          component: Resume,
          suffix: 'resumeScore'
        },
        {
          pageType: PAGE_TYPE.ACTIVITY_TRACKER,
          component: ActivityTracker,
          props: {
            get prefetchedActivityData () { return this.activityTrackerData }
          },
          suffix: 'appliedJobsCount',
          ref: 'activityTracker'
        },
        {
          pageType: PAGE_TYPE.TAG,
          component: Tag,
          permission: { strict: false, any: ['PREMIUM_TAG_MANAGEMENT', 'PREMIUM_CRM_PARSE_RESUME_SDK_TAG'] }
        }
      ]
      const hidePageType = []
      if (this.candidateInfo?.is_agency_uploaded_and_hid) {
        hidePageType.push(PAGE_TYPE.TAG)
      }
      return _.chain(tabList).filter(tab => {
        if (hidePageType.includes(tab.pageType)) {
          return false
        }
        if (tab.permission) {
          return this.hasAnyPermission(tab.permission)
        }
        return true
      }).map(tab => {
        const pageTypeKey = _.findKey(PAGE_TYPE, pageType => pageType === tab.pageType)
        tab.labelI18nKey = `${this.i18nPrefix}.tabMenu.${pageTypeKey}`
        return tab
      }).value()
    },
    pageType () {
      return this.$route.params.pageType
    },
    candidateId () {
      return this.$route.query.candidate_id
    },
    talentId () {
      return this.$route.params.talentId
    },
    listPageParams () {
      return this.$route.query.list_params
    },
    pageTitle () {
      const title = this.$t(`${this.i18nPrefix}.title.detailResume`)
      const tabLabel = this.activeTab && this.$t(this.activeTab.labelI18nKey)
      const candidateName = this.candidateInfo?.other_name
      return _.chain([candidateName, title, tabLabel]).filter().join(' - ').value()
    },
    applicationList () {
      return [{
        id: 3999090,
        job: {
          ...this.candidateInfo.job,
          company: this.candidateInfo.company
        },
        candidate: this.candidateInfo.candidate
      }]
    },
    totalCount () {
      return this.talentIdList.length
    },
    currentTalentIdIndex () {
      return _.indexOf(this.talentIdList, this.candidateInfo.id)
    }
  },
  created () {
    this.$eventBus.$on('showCollapseInfo', (activeNameList) => this.showCollapseInfo(activeNameList))
    this.$eventBus.$on('changeIsMyCommentsStatus', (status) => {
      this.isMyComment = status
    })
    this.$eventBus.$on('onCommentAdded', (comment, callback) => this.onCommentAdded(comment, callback))
    this.refreshActiveTab()
    this.pageDataLoader = this.initPageData()
    this.loadTalentIdList()
  },
  mounted () {
    this.pageDataLoader.then(() => {
      this.$nextTick(() => {
        this.resetFixedNavigationRight()
      })
    })
    this.fixedNavigationRightResetter = _.debounce(this.resetFixedNavigationRight, 10)
    window.addEventListener('resize', this.fixedNavigationRightResetter)
  },
  destroyed () {
    window.removeEventListener('resize', this.fixedNavigationRightResetter)
  },
  methods: {
    ...mapActions('talent', ['getTalentCandidateBaseInfo', 'getTalentCandidateIdList', 'getTalentCandidateActivityInfo']),
    resetFixedNavigationRight () {
      const rect = this.$refs.detailInfoContainer.getBoundingClientRect()
      const right = window.innerWidth - rect.left - rect.width + 68
      this.fixedNavigationRight = _.max([right, 104])
    },
    refreshActiveTab () {
      if (!this.pageType) {
        const pageType = this.tabList[0].pageType
        this.$router.push({ path: `/v-admin/talent-pool/candidate/profile/${pageType}`, query: this.$route.query })
        return
      }
      if (this.activeTab && this.activeTab.pageType === this.pageType) {
        return
      }
      const tab = _.find(this.tabList, { pageType: this.pageType })
      if (!tab) {
        const pageType = this.tabList[0].pageType
        this.$router.replace({ path: `/v-admin/talent-pool/candidate/profile/${pageType}`, query: this.$route.query })
      } else if (tab.permission && !this.hasAnyPermission(tab.permission)) {
        this.$router.replace('/error/no-permission')
      } else {
        this.activeTab = tab
      }
    },
    async loadCandidateProfileInfo () {
      const resp = await this.getTalentCandidateBaseInfo({ candidateId: this.candidateId })
      if (resp) {
        const mobile = resp.mobile || resp.second_phone || resp.account_mobile
        const countryCode = resp.country_code
        const age = moment().diff(resp.birthday, 'year')
        createI18nProperty(resp, 'yearsOld', `${this.i18nPrefix}.label.yearsOld`, { age })
        resp.mobileWithCountryCode = _.chain([countryCode, mobile]).filter().join(' ').value()
        addI18nProperty(resp.root_company, 'i18nName', 'cn_name', 'name')
        this.candidateInfo = resp
      }
    },
    async loadTalentIdList () {
      if (!this.listPageParams) {
        return
      }
      this.candidateIdListLoading = true
      const params = {
        list_params: this.listPageParams
      }
      try {
        const resp = await this.getTalentCandidateIdList(params)
        if (resp) {
          this.talentIdList = resp
        }
      } finally {
        this.candidateIdListLoading = false
      }
    },
    async loadActivityTrackerSummary () {
      const resp = await this.getTalentCandidateActivityInfo({ candidateId: this.candidateId })
      if (resp) {
        const candidateAppliedJobs = this.normalizeAppliedJobInfo(resp.candidate_applied_jobs)
        this.candidateAppliedJobs = candidateAppliedJobs
        this.activityTrackerData = {
          ...resp,
          candidate_applied_jobs: candidateAppliedJobs
        }
      } else {
        this.candidateAppliedJobs = []
        this.activityTrackerData = null
      }
      this.appliedJobsLoaded = true
    },
    normalizeAppliedJobInfo (appliedJobs) {
      return _.chain(appliedJobs).map(item => {
        addI18nProperty(item.job, 'i18nName', 'cn_name', 'name')
        addI18nProperty(item.company, 'i18nName', 'cn_name', 'name')
        addI18nProperty(item.status, 'i18nName', 'name_cn', 'name_en')
        return item
      }).value()
    },
    async initPageData () {
      this.$eventBus.$emit('showLoading', true)
      this.loading = true
      this.appliedJobsLoaded = false
      this.candidateAppliedJobs = []
      this.activityTrackerData = null
      try {
        // this.setApplicationViewed()
        await Promise.all([this.loadCandidateProfileInfo(), this.loadActivityTrackerSummary()])
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    onTabChange (tab) {
      const pageType = tab.pageType
      const path = `/v-admin/talent-pool/candidate/profile/${pageType}`
      if (this.$route.path === path) {
        return
      }
      this.$router.push({ path, query: this.$route.query })
    },
    handleToRejectStatus () {
      if (this.candidateInfo?.root_company?.id === ROOT_COMPANY_ID.MSD) {
        this.defaultStatusKey = null
      } else {
        this.defaultStatusKey = 'Disqualified'
      }
      this.$refs.changeStatusDialog.showDialog()
    },
    handleChangeStatus (status) {
      this.defaultStatusKey = status.key
      this.$refs.changeStatusDialog.showDialog()
    },
    handleBreadCrumbNavigationClick () {
      let url = '/v-admin/talent-pool/group-list'
      const query = this.$route.query
      if (query.back) {
        url = query.back
      }
      this.$router.push(url)
    },
    handlePrevBtnClick () {
      if (this.candidateIdListLoading) {
        return
      }
      const candidateId = this.talentIdList[this.currentTalentIdIndex - 1]
      if (!candidateId) {
        return
      }
      this.$router.push({
        path: '/v-admin/talent-pool/candidate/profile/resume',
        query: {
          ...this.$route.query,
          candidate_id: candidateId
        }
      })
    },
    handleNextBtnClick () {
      if (this.candidateIdListLoading) {
        return
      }
      const candidateId = this.talentIdList[this.currentTalentIdIndex + 1]
      if (!candidateId) {
        return
      }
      this.$router.push({
        path: '/v-admin/talent-pool/candidate/profile/resume',
        query: {
          ...this.$route.query,
          candidate_id: candidateId
        }
      })
    },
    onCommentAdded (comment, callback) {
      const activityTracker = this.$refs.activityTracker
      const collapseInfo = this.$refs.collapseInfo
      if (activityTracker) {
        activityTracker.onCommentAdded(comment)
      }
      if (collapseInfo) {
        collapseInfo.onCommentAdded(comment)
      }
      if (callback) {
        callback()
      }
    },
    showCollapseInfo (activeNameList) {
      const collapseInfo = this.$refs.collapseInfo
      if (collapseInfo) {
        collapseInfo.handelChangeShowCollapse(activeNameList)
      }
    },
    handleOpen (collapseWidth) {
      if (!this.showCollapse) {
        this.showCollapse = true
        this.fixedNavigationRight += collapseWidth
      }
    },
    handleClose (collapseWidth) {
      if (this.showCollapse) {
        this.showCollapse = false
        this.fixedNavigationRight -= collapseWidth
      }
    }
  },
  watch: {
    pageType () {
      this.refreshActiveTab()
    },
    candidateId () {
      this.initPageData()
    }
  }
}
</script>

<style lang="scss" scoped>
.profile-info-page {
  padding: 20px 0;
  display: flex;
  height: 100%;

  .profile-info {
    flex: 1;
    display: flex;
  }

  &.collapse {
    padding-bottom: 0;

    .profile-info {
      margin-left: 12px;
      overflow: auto;

      ::v-deep {
        .tab-list {
          .tab-item {

          }
        }
      }
    }

    .collapse-container {
      margin-left: 12px;
    }
  }
}
.profile-info-container {
  display: flex;
  flex-direction: column;
  width: 95%;
  min-width: min-content;
  margin: 50px auto 0;
  position: relative;

  .breadcrumb-navigation {
    line-height: 25px;
    font-size: 18px;
    color: #606266;
    position: absolute;
    top: -47px;
    left: 0;

    .split {
      margin: 0 10px;
    }

    .navigation {
      cursor: pointer;
      color: #0096cc;

      &.active {
        cursor: default;
        color: #606266;
      }
    }
  }

  .application-list-navigation {
    position: absolute;
    top: -50px;
    right: 0;

    .navigation-btn {
      padding: 0 8px;
      line-height: 28px;
      border-radius: 2px;
      border-color: #c8c8c8;

      .btn-icon {
        font-size: 12px;
      }
    }

    .position  {
      margin: 0 56px;
      line-height: 25px;
      font-size: 18px;
      color: #606266;

      .split {
        margin: 0 10px;
      }
    }

    .loading {
      position: absolute;
      left: 0;
      top: 0;

      ::v-deep .spinner {
        height: 30px;
      }
    }
  }

  .base-info-container {
    padding: 30px 40px;
    border-radius: 6px;
    background: #fff;
  }

  .detail-info-container {
    flex: 1;
    margin-top: 20px;
    padding: 25px;
    border-radius: 6px;
    background: #fff;

    .info-tab-card {
      min-height: 100%;
      .score-container {
        position: absolute;
        right: -2px;
        top: 0;
        transform: translate(0, -50%);

        .score-item {
          display: inline-block;
          width: 30px;
          height: 30px;
          line-height: 26px;
          border: 2px solid #0096cc;
          border-radius: 50%;
          background-color: #fff;
          color: #0096cc;
          text-align: center;
          font-weight: bold;
          font-size: 14px;

          & + .score-item{
            margin-left: 5px;
          }
        }
      }
    }
  }

  .fixed-list-navigation {
    position: fixed;
    bottom: 48px;
    right: 124px;

    .navigation-btn {
      width: 40px;
      height: 40px;
      padding: 0 8px;
      border-radius: 50%;
      border: 0;
      box-shadow: 0px 2px 4px 0px rgba(0,0,0,0.06), 0px 3px 6px 0px rgba(0,0,1,0.12);
      text-align: center;
      background: #ffffff;

      & + .navigation-btn {
        margin-left: 38px;
      }

      &:not(.is-disabled):hover {
        background: #0096cc;

        .btn-icon {
          color: #ffffff;
        }
      }

      &.prev-btn {
        .btn-icon {
          transform: rotateZ(180deg);
        }
      }

      .btn-icon {
        display: inline-block;
        font-size: 15px;
        color: #606266;

        &::before {
          padding-left: 2px;
        }
      }
    }
  }
}
.applied-jobs-tooltip {
  .applied-jobs-tooltip-content {
    width: 240px;
    max-height: 240px;
    overflow-y: auto;
    color: #303133;
    line-height: 20px;

    .tooltip-line {
      white-space: normal;
      word-break: break-word;
    }

    .section-title {
      margin-top: 8px;
    }

    .tooltip-job-item {
      display: flex;
      align-items: flex-start;
      margin-top: 4px;
      white-space: normal;

      .dot {
        flex-shrink: 0;
        width: 1em;
        margin-right: 4px;
        color: inherit;
        font: inherit;
        line-height: inherit;
        text-align: center;
        color: black;

        &::before {
          content: '\2022';
        }
      }
    }
  }
}
</style>
